import React, { useState, useEffect, useRef } from 'react';
import Footer from '../components/Footer';
import { calculateReadTime } from '../utils/helpers';
import { generateBlogPostSchema, injectStructuredData } from '../utils/blogSchema';

const BlogDetail = ({ post, navigateToHome, navigateToBlog, navigateToB2B, openShareModal, backendUrl, similarPosts, onViewBlogPost, navigateToPrivacy, navigateToTerms, navigateToAbout, navigateToSitemap }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // Ref to prevent duplicate similar post loading
  const loadingSimilarRef = useRef(null);
  
  // Sidebar states
  const [latestPosts, setLatestPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchError, setSearchError] = useState('');
  
  // Use ref for sidebar loading state to prevent re-renders
  const sidebarLoadingRef = useRef(false);
  
  // Memoize similar posts calculation to avoid constant re-renders
  const currentPostId = post?.id;
  
  // Generate and inject structured data when component mounts
  useEffect(() => {
    if (post) {
      const schema = generateBlogPostSchema(post);
      injectStructuredData(schema);
      
      // Set page title and meta description for SEO
      document.title = `${post.title} - FogPortál.hu | Fogászati blog`;
      
      // Update meta description
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', post.excerpt || post.meta_description || post.title);
      } else {
        const newMetaDescription = document.createElement('meta');
        newMetaDescription.name = 'description';
        newMetaDescription.content = post.excerpt || post.meta_description || post.title;
        document.head.appendChild(newMetaDescription);
      }
      
      // Update OpenGraph meta tags using the new utility
      updateOpenGraphTags({
        title: post.title,
        description: post.excerpt || post.meta_description || post.title,
        type: 'article',
        url: `${window.location.origin}/blog/${post.slug || post.id}`,
        image: post.featured_image,
        imageAlt: `${post.title} - FogPortál.hu blog cikk kép`
      }, publicSettings.default_og_image);
    }
    
    // Cleanup function to remove structured data when component unmounts
    return () => {
      const existingScript = document.querySelector('script[data-type="blog-structured-data"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [post]);
  
  // TEMPORARILY DISABLE CATEGORIES LOADING - ALSO CAUSING INFINITE LOOP
  // TODO: Fix React re-rendering issues in BlogDetail component
  /*
  // Load categories once when component mounts (fixed infinite loop)
  useEffect(() => {
    if (!backendUrl || categories.length > 0) {
      return; // Skip if no backendUrl or categories already loaded
    }
    
    const loadCategories = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/blog/categories`);
        if (response.ok) {
          const categoriesData = await response.json();
          setCategories(Array.isArray(categoriesData) ? categoriesData : []);
          console.log('Categories loaded:', Array.isArray(categoriesData) ? categoriesData.length : 0);
        }
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };
    
    loadCategories();
  }, []); // Empty dependency array - run only once on mount
  */
  
  // TEMPORARILY DISABLE SIDEBAR DATA LOADING - INFINITE LOOP ISSUE
  // TODO: Investigate and fix the infinite loop in useEffect
  /*
  useEffect(() => {
    // Only load if we haven't loaded data yet and not currently loading
    if (!backendUrl || sidebarLoadingRef.current || categories.length > 0 || latestPosts.length > 0) {
      return;
    }
    
    console.log('LOADING: Sidebar data (latest posts and categories)...');
    sidebarLoadingRef.current = true;
    
    const loadData = async () => {
      try {
        // Load latest posts and categories in parallel
        const [postsResponse, categoriesResponse] = await Promise.all([
          fetch(`${backendUrl}/api/blog?limit=5`),
          fetch(`${backendUrl}/api/blog/categories`)
        ]);
        
        if (postsResponse.ok) {
          const posts = await postsResponse.json();
          setLatestPosts(posts);
          console.log('Latest posts loaded:', posts.length);
        }
        
        if (categoriesResponse.ok) {
          const categoriesData = await categoriesResponse.json();
          setCategories(Array.isArray(categoriesData) ? categoriesData : []);
          console.log('Categories loaded:', Array.isArray(categoriesData) ? categoriesData.length : 0);
        }
      } catch (error) {
        console.error('Error loading sidebar data:', error);
      } finally {
        sidebarLoadingRef.current = false;
      }
    };
    
    loadData();
  }, []); // Empty dependency array - run only on mount
  */
  
  // Load similar posts when component mounts or post ID changes  
  useEffect(() => {
    if (!currentPostId || loadingSimilarRef.current === currentPostId || !backendUrl) {
      return; // Skip if no post ID or already loading for this post
    }
    
    // Set loading ref to current post ID to prevent duplicate loads
    loadingSimilarRef.current = currentPostId;
    
    const loadSimilar = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/blog?limit=100`);
        if (!response.ok) return;
        
        const allPosts = await response.json();
        const otherPosts = allPosts.filter(p => p.id !== currentPostId);
        
        let similar = [];
        
        // Get posts from same category
        if (post.categories?.length > 0) {
          similar = otherPosts.filter(p => 
            p.categories?.some(cat => post.categories.includes(cat))
          ).slice(0, 3);
        }
        
        // Fill with other posts if needed
        if (similar.length < 3) {
          const remaining = otherPosts
            .filter(p => !similar.some(s => s.id === p.id))
            .sort((a, b) => a.id.localeCompare(b.id)) // Stable sort
            .slice(0, 3 - similar.length);
          similar = [...similar, ...remaining];
        }
        
        setSimilarPosts(similar);
      } catch (error) {
        console.error('Error loading similar posts:', error);
        setSimilarPosts([]);
      }
    };
    
    loadSimilar();
  }, [currentPostId]); // Remove backendUrl and post.categories from dependencies
  
  // Blog search functionality with debounce to prevent rate limiting
  const [searchTimeout, setSearchTimeout] = useState(null);
  
  const handleBlogSearch = async (e) => {
    e.preventDefault();
    
    if (!searchQuery || searchQuery.trim().length < 2) {
      setSearchError('A keresési kifejezés legalább 2 karakter hosszú legyen!');
      return;
    }
    
    setSearchError('');
    
    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // Set new timeout to prevent too frequent requests
    const timeout = setTimeout(async () => {
      try {
        const response = await fetch(`${backendUrl}/api/blog/search`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ query: searchQuery.trim() })
        });
        
        if (response.status === 429) {
          setSearchError('Túl sok keresési kérés. Kérjük várjon egy kicsit!');
          return;
        }
        
        if (response.ok) {
          const results = await response.json();
          setSearchResults(results);
        } else {
          const error = await response.json();
          setSearchError(error.detail || 'Hiba történt a keresés során');
        }
      } catch (error) {
        setSearchError('Hálózati hiba történt');
        console.error('Search error:', error);
      }
    }, 500); // 500ms debounce
    
    setSearchTimeout(timeout);
  };
  
  if (!post) return null;
  
  return (
    <div className="min-h-screen bg-white">
      {/* Modern Header */}
      <header className="bg-white shadow-lg border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <div className="flex items-center">
              <button 
                onClick={navigateToHome}
                className="flex items-center space-x-3"
              >
                <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-green-600 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 1C7.6 1 4 4.6 4 9c0 2.2.9 4.2 2.3 5.7C7.1 15.4 8 16.7 8 18c0 .6.4 1 1 1s1-.4 1-1c0-1.8-1.1-3.4-2.8-4.1C6 12.1 5.5 10.6 5.5 9c0-3.6 2.9-6.5 6.5-6.5s6.5 2.9 6.5 6.5c0 1.6-.5 3.1-1.7 4.9C15 15.6 14 17.2 14 18c0 .6.4 1 1 1s1-.4 1-1c0-1.3.9-2.6 1.7-3.3C19.1 13.2 20 11.2 20 9c0-4.4-3.6-8-8-8zm0 4c-2.2 0-4 1.8-4 4 0 1.1.4 2.1 1.2 2.8.5.4.8 1 .8 1.6 0 .6-.4 1-1 1s-1-.4-1-1c0-.4-.2-.7-.5-.9C6.2 11.7 6 10.9 6 10c0-3.3 2.7-6 6-6s6 2.7 6 6c0 .9-.2 1.7-1.5 2.5-.3.2-.5.5-.5.9 0 .6-.4 1-1 1s-1-.4-1-1c0-.6.3-1.2.8-1.6.8-.7 1.2-1.7 1.2-2.8 0-2.2-1.8-4-4-4z"/>
                  </svg>
                </div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
                  FogPortál.hu
                </h1>
              </button>
            </div>

            {/* Navigation */}
            <nav className="hidden md:flex items-center space-x-1">
              <button 
                onClick={navigateToHome}
                className="px-4 py-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-lg font-medium text-sm transition-all duration-200"
              >
                Főoldal
              </button>
              <button 
                onClick={navigateToBlog}
                className="px-4 py-2 text-blue-600 bg-blue-50 rounded-lg font-medium text-sm shadow-sm"
              >
                Blog
              </button>
              <button 
                onClick={navigateToB2B}
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-green-600 text-white rounded-lg font-medium text-sm hover:from-blue-700 hover:to-green-700 transition-all duration-200 shadow-md hover:shadow-lg ml-2"
              >
                Megjelenés fogászatoknak
              </button>
            </nav>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button 
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-lg text-gray-600 hover:text-blue-600 hover:bg-gray-50"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} 
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
        
        {/* Mobile menu dropdown */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-gray-200">
            <div className="flex flex-col space-y-2 pt-4 px-4">
              <button 
                onClick={() => {
                  navigateToHome();
                  setIsMobileMenuOpen(false);
                }}
                className="px-4 py-3 text-left text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-lg font-medium transition-all duration-200"
              >
                Főoldal
              </button>
              <button 
                onClick={() => {
                  navigateToBlog();
                  setIsMobileMenuOpen(false);
                }}
                className="px-4 py-3 text-left text-blue-600 bg-blue-50 rounded-lg font-medium hover:bg-blue-100 transition-all duration-200 shadow-sm"
              >
                Blog
              </button>
              <button 
                onClick={() => {
                  navigateToB2B();
                  setIsMobileMenuOpen(false);
                }}
                className="px-4 py-3 text-left text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-lg font-medium transition-all duration-200"
              >
                Megjelenés fogászatoknak
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Main Content Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          
          {/* Left Column - Article Content */}
          <article className="lg:col-span-3">
            {/* Article Header */}
            <header className="mb-12">
              <div className="flex items-center space-x-4 mb-6">
                <span className="bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium">
                  {(post.categories && post.categories.length > 0) ? post.categories[0] : 'Fogászati Tanácsok'}
                </span>
                <span className="text-gray-500 text-sm">
                  {new Date(post.published_at || post.created_at).toLocaleDateString('hu-HU')}
                </span>
              </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            {post.title}
          </h1>
          
          <p className="text-xl text-gray-600 mb-8 leading-relaxed">
            {post.excerpt}
          </p>
          
          <div className="flex items-center justify-between border-b border-gray-200 pb-8">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                <span className="text-white font-semibold text-lg">
                  {post.author.charAt(0)}
                </span>
              </div>
              <div>
                <p className="font-semibold text-gray-900">{post.author}</p>
                <p className="text-sm text-gray-500">Fogászati szakértő</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-6 text-sm text-gray-500">
              <span>{calculateReadTime(post.content)} perc olvasás</span>
              <div className="flex items-center space-x-3">
                <button 
                  onClick={() => openShareModal(post.title)}
                  className="text-blue-600 hover:text-blue-800 font-medium transition-colors"
                >
                  📤 Megosztás
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Featured Image */}
        {post.featured_image ? (
          <div className="mb-12">
            <div className="aspect-video rounded-xl overflow-hidden">
              <img 
                src={`${backendUrl}${post.featured_image}`} 
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        ) : (
          <div className="mb-12">
            <div className="aspect-video bg-gradient-to-br from-blue-100 to-green-100 rounded-xl flex items-center justify-center">
              <div className="text-center">
                <div className="w-24 h-24 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="text-gray-600">Fogászati cikk illusztráció</p>
              </div>
            </div>
          </div>
        )}

        {/* Article Content */}
        <div className="prose prose-lg max-w-none">
          <div 
            className="text-gray-800 leading-relaxed space-y-6 quill-content"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </div>

        {/* Similar Posts Section */}
        {similarPosts.length > 0 && (
          <section className="mt-16 pt-8 border-t border-gray-200">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Hasonló cikkek</h2>
              <p className="text-gray-600">További érdekes írásaink a fogászat világából</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {similarPosts.map(similarPost => (
                <article 
                  key={similarPost.id}
                  onClick={() => onViewBlogPost(similarPost.id, similarPost.slug)}
                  className="bg-white rounded-xl shadow-md overflow-hidden cursor-pointer"
                >
                  {similarPost.featured_image ? (
                    <div className="aspect-video overflow-hidden">
                      <img 
                        src={`${backendUrl}${similarPost.featured_image}`} 
                        alt={similarPost.title}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  ) : (
                    <div className="aspect-video bg-gradient-to-br from-blue-100 to-green-100 flex items-center justify-center">
                      <div className="text-center">
                        <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-2">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <p className="text-xs text-gray-600">Blogcikk</p>
                      </div>
                    </div>
                  )}
                  
                  <div className="p-4">
                    <h3 className="text-lg font-bold text-gray-900 line-clamp-2">
                      {similarPost.title}
                    </h3>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* Article Footer */}
        <footer className="mt-16 pt-8 border-t border-gray-200">
          <div className="text-center">
            <button 
              onClick={navigateToBlog}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold"
            >
              Összes cikk megtekintése →
            </button>
          </div>
        </footer>
      </article>
      
      {/* Right Sidebar - TEMPORARILY COMPLETELY DISABLED */}
      {/*
      <aside className="lg:col-span-1 space-y-6">
        
        {/* Categories Widget - TEMPORARILY DISABLED *//*}
        {/*
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Kategóriák</h3>
          <div className="space-y-2">
            {categories.length === 0 ? (
              <p className="text-gray-500 text-sm">Kategóriák betöltése...</p>
            ) : (
              categories.slice(0, 8).map(category => (
                <button
                  key={category.id}
                  onClick={() => {
                    // Navigate to blog with category filter
                    window.location.href = `/blog?category=${encodeURIComponent(category.name)}`;
                  }}
                  className="block w-full text-left text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 px-2 py-1 rounded transition-colors"
                >
                  {category.name}
                </button>
              ))
            )}
          </div>
        </div>
        *//*}
        
        {/* Latest Posts Widget - Re-enabled with safe loading *//*}
        {latestPosts.length > 0 && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Új cikkek</h3>
            <div className="space-y-4">
              {latestPosts
                .filter(latestPost => latestPost.id !== currentPostId) // Filter out current post
                .slice(0, 4)
                .map(latestPost => (
                <article 
                  key={latestPost.id}
                  onClick={() => onViewBlogPost(latestPost.id, latestPost.slug)}
                  className="flex items-start space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                >
                  {latestPost.featured_image ? (
                    <div className="w-16 h-12 flex-shrink-0 overflow-hidden rounded-md">
                      <img 
                        src={`${backendUrl}${latestPost.featured_image}`} 
                        alt={latestPost.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-16 h-12 flex-shrink-0 bg-gradient-to-br from-blue-100 to-green-100 rounded-md flex items-center justify-center">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-gray-900 line-clamp-2 leading-snug">
                      {latestPost.title}
                    </h4>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(latestPost.published_at || latestPost.created_at).toLocaleDateString('hu-HU')}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}

        {/* Search Widget *//*}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Keresés</h3>
          <form onSubmit={handleBlogSearch} className="space-y-3">
            <div>
              <input
                type="text"
                placeholder="Keresés a blogban..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
            <button
              type="submit"
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              🔍 Keresés
            </button>
          </form>
          
          {searchError && (
            <div className="mt-3 bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded text-sm">
              {searchError}
            </div>
          )}
          
          {searchResults && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-900 mb-2">
                Találatok ({searchResults.total})
              </h4>
              {searchResults.posts.length > 0 ? (
                <div className="space-y-2">
                  {searchResults.posts.slice(0, 3).map(resultPost => (
                    <article 
                      key={resultPost.id}
                      onClick={() => onViewBlogPost(resultPost.id, resultPost.slug)}
                      className="cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                    >
                      <h5 className="text-sm font-medium text-gray-900 line-clamp-2">
                        {resultPost.title}
                      </h5>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(resultPost.published_at || resultPost.created_at).toLocaleDateString('hu-HU')}
                      </p>
                    </article>
                  ))}
                  {searchResults.posts.length > 3 && (
                    <button
                      onClick={() => window.location.href = `/blog?search=${encodeURIComponent(searchQuery)}`}
                      className="w-full text-blue-600 hover:text-blue-800 text-sm text-center py-2"
                    >
                      Összes találat megtekintése →
                    </button>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500">Nincs találat a keresett kifejezésre.</p>
              )}
            </div>
          )}
        </div>
        
      </aside>
      */}
      
      </div>
      </div>
      
      {/* Footer */}
      <Footer 
        navigateToHome={navigateToHome}
        navigateToBlog={navigateToBlog}
        navigateToB2B={navigateToB2B}
        navigateToPrivacy={() => window.location.href = '/adatvedelem'}
        navigateToTerms={() => window.location.href = '/aszf'}
        navigateToAbout={() => window.location.href = '/rolunk'}
        navigateToSitemap={navigateToSitemap}
      />
    </div>
  );
};



export default BlogDetail;
